import React, { FC } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { getTradingPlatform, PlatformBtnText } from '@monorepo/client-common';
import { Environments, TradingPlatforms } from '@monorepo/client-types';
import { SystemIcon } from '@monorepo/nova-icons';
import { Button } from '@monorepo/nova-ui';
import { TradingSystem } from '@monorepo/types';
import { PlatformIcon } from './trading-system-btn';

interface TradingPlatformsButtonsProps {
  tradingSystem: TradingSystem;
  containerSx?: SxProps<Theme>;
  buttonSx?: SxProps<Theme>;
  gap?: number;
  onPlatformClick?: (link: string, platform: string) => void;
  showDownloadIcon?: boolean;
  showPlatformIcon?: boolean;
}

const defaultPlatformDownload = (link: string): void => {
  window.open(link, '_blank');
};

export const TradingPlatformsButtons: FC<TradingPlatformsButtonsProps> = ({
  tradingSystem,
  containerSx,
  buttonSx,

  gap = 4,
  onPlatformClick,
  showDownloadIcon = true,
  showPlatformIcon = true,
}) => {
  const isProduction = (() => {
    // Check for Vite environment variable
    if (typeof import.meta !== 'undefined' && 'env' in import.meta) {
      const importMeta = import.meta as unknown as {
        env: Record<string, string>;
      };
      return importMeta.env['VITE_APP_ENV'] === Environments.Production;
    }

    // Fallback to Node.js environment
    if (typeof process !== 'undefined' && process.env) {
      return process.env['NODE_ENV'] === 'production';
    }

    return false;
  })();

  const platforms = getTradingPlatform(tradingSystem, isProduction);

  const handlePlatformClick = (link: string, platform: string) => {
    if (onPlatformClick) {
      onPlatformClick(link, platform);
    } else {
      defaultPlatformDownload(link);
    }
  };

  return (
    <Box
      gap={gap}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        ...containerSx,
      }}
    >
      {platforms.map((platformData, index) => (
        <Button
          fullWidth
          key={`${platformData.platform}-${index}`}
          variant="outlined"
          sx={buttonSx}
          onClick={() =>
            handlePlatformClick(platformData.link, platformData.platform)
          }
          startIcon={
            showPlatformIcon ? PlatformIcon[platformData.platform] : undefined
          }
          endIcon={
            showDownloadIcon ? (
              <SystemIcon
                iconName={
                  platformData.platform === TradingPlatforms.Web
                    ? 'CaretRight'
                    : 'DownloadSimple'
                }
              />
            ) : undefined
          }
        >
          {PlatformBtnText[platformData.platform]}
        </Button>
      ))}
    </Box>
  );
};
